/**
 * Mounts once at the App layout. For @a2zradiology.ai users only, Alt+right-
 * click (Option+right-click on macOS) anywhere opens the feedback composer
 * anchored at the cursor.
 *
 * Alt, not Shift: Firefox never dispatches `contextmenu` to the page when
 * Shift is held (anti-hijack escape hatch, dom.event.contextmenu.
 * shift_suppresses_event defaults true), so a Shift-based trigger can't work
 * there. Alt+right-click fires normally in every browser, and Cornerstone
 * binds no tool to Alt+Secondary (bindings match modifiers exactly).
 *
 * Both listeners are document-level CAPTURE phase so they pre-empt every
 * existing right-click consumer (Cornerstone's mousedown pipeline, the
 * viewport context menu, and the reporter's bubble-phase `contextmenu`
 * menus). `stopImmediatePropagation` keeps the gesture from reaching any of
 * them; un-modified right-clicks pass through untouched.
 */

import { useCallback, useEffect, useState } from "react";

import { isLocalDevMode } from "@/config/cognito";
import { useUserId } from "@/context/AuthContext";

import { FeedbackComposer } from "./FeedbackComposer";
import { captureTarget, type CapturedTarget } from "./feedbackCapture";

const INTERNAL_DOMAIN = "@a2zradiology.ai";

interface Trigger {
  x: number;
  y: number;
  target?: CapturedTarget;
}

export function FeedbackHost() {
  const userId = useUserId();
  // Mirror the backend's _is_internal gate: arm for @a2zradiology.ai users and
  // in local dev (whose seeded userId is a Cognito sub, not an email).
  const isInternal = isLocalDevMode() || (!!userId && userId.endsWith(INTERNAL_DOMAIN));
  const [trigger, setTrigger] = useState<Trigger | null>(null);

  useEffect(() => {
    if (!isInternal) return;

    // Keep Cornerstone's mousedown pipeline (annotation hit-testing,
    // MOUSE_DOWN events) from seeing the gesture; the composer owns
    // Alt+right-click.
    const onMouseDown = (e: MouseEvent) => {
      if (e.altKey && e.button === 2) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    const onContextMenu = (e: MouseEvent) => {
      if (!e.altKey) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const next: Trigger = { x: e.clientX, y: e.clientY };
      const target = captureTarget(e.target);
      if (target) next.target = target;
      setTrigger(next);
    };

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
    };
  }, [isInternal]);

  const close = useCallback(() => setTrigger(null), []);

  if (!trigger) return null;
  return (
    <FeedbackComposer
      anchor={{ x: trigger.x, y: trigger.y }}
      target={trigger.target}
      onClose={close}
    />
  );
}
